// Card statistics for the deck builder (the Card Stats toggle): how often each card is in the decks played
// online, and how often those decks win. The game server keeps them (GET /stats); this fetches them, and
// fetches them again when they are more than a minute old.
import type { CardStats } from '../shared/online.ts';

let cached: { at: number; stats: CardStats } | null = null;

/** The latest card statistics, or null if the game server can't be reached (the game wasn't opened from it). */
export async function loadCardStats(): Promise<CardStats | null> {
  if (cached && Date.now() - cached.at < 60_000) return cached.stats;
  try {
    const res = await fetch('/stats', { cache: 'no-store' });
    if (!res.ok) return cached?.stats ?? null;
    const stats = (await res.json()) as CardStats;
    if (typeof stats?.decks !== 'number' || typeof stats.cards !== 'object') return cached?.stats ?? null;
    cached = { at: Date.now(), stats };
    return stats;
  } catch {
    return cached?.stats ?? null;
  }
}

/**
 * One card's numbers (shares from 0 to 1; null = no games to tell from yet):
 *   use: of the decks whose games are over, the share it was actually played from
 *   win: of the games it was played in that had a winner, the share won
 *   inDecks: the share of decks it was in ("used in decks"), played or not
 *   deckWin: of the games its decks were in that had a winner, the share won ("used in winning decks")
 */
export function statsFor(stats: CardStats, num: number) {
  const c = stats.cards[num];
  const decks = c?.decks ?? 0;
  const played = c?.played ?? 0;
  const playedDecided = c?.playedDecided ?? 0;
  const playedWon = c?.playedWon ?? 0;
  const playDecks = stats.playDecks ?? 0;
  return {
    played,
    playDecks,
    use: playDecks > 0 ? played / playDecks : 0,
    playedDecided,
    playedWon,
    win: playedDecided > 0 ? playedWon / playedDecided : null,
    decks,
    inDecks: stats.decks > 0 ? decks / stats.decks : 0,
    decided: c?.decided ?? 0,
    won: c?.won ?? 0,
    deckWin: c && c.decided > 0 ? c.won / c.decided : null,
  };
}
